import {
    ApplicationRef,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChange,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {QuickReplyDto} from '../dto/QuickReplyDto';
import {DialogViewModelCreatorService} from '../service/dialog-view-model-creator.service';
import {DialogViewModel} from '../viewmodel/DialogViewModel';
import {CommunicationService} from '../service/communication.service';
import {BotatoChatManagerService} from '../service/botato-chat-manager.service';
import {MessageGroupViewModel} from '../viewmodel/MessageGroupViewModel';
import {Subscription} from 'rxjs';
import {MessageViewModel} from '../viewmodel/MessageViewModel';
import {
    DialogConfigInputOptionType,
    DialogConfigInputType
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/DialogConfig/DialogConfig.js';
import {
    InputSourceType
} from 'botato-botengine-browser/botato/botato-botengine/source/InstantMessagingAdapter/WebSocket/dto/InputSourceType.js';

@Component({
    selector: 'botato-chat',
    templateUrl: './botato-chat.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class BotatoChatComponent implements OnInit, OnDestroy, OnChanges {
    public isConnected: boolean | undefined;
    public dialogViewModel: DialogViewModel = new DialogViewModel();
    public showEndOfHistoryIndicator: boolean | undefined;
    // TODO how to implement this feature and why is this needed? -> Noora!
    @Input()
    public inputFocusable: boolean = true;
    @Input()
    public inputComponentFocus: boolean | undefined;
    @Input()
    public initialInputType: DialogConfigInputType = 'text';
    @Input()
    public clientId: string | undefined;
    @Input()
    public botId: string | undefined;
    @Input()
    public initialInputOptions: DialogConfigInputOptionType | undefined;
    @Input()
    public endOfHistoryMessage: string | undefined;
    @Output()
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    public onQuickReplyClick = new EventEmitter<QuickReplyDto>();
    @ViewChild('dialog', {static: true}) private dialogDiv!: ElementRef;
    private subscription = new Subscription();

    constructor(private communicationService: CommunicationService,
                public dialogViewModelCreator: DialogViewModelCreatorService,
                private chatManagerService: BotatoChatManagerService,
                private applicationRef: ApplicationRef,
                private changeDetectorRef: ChangeDetectorRef) {

        // Initial tick to display data and run ngOnInit
        setTimeout(() => {
            applicationRef.tick();
        });
    }

    public get inputType(): string {
        if (this.communicationService.lastBotResult?.webSocketSpecificData?.botResultOptions?.promptOptions?.input?.type !== undefined) {
            return this.communicationService.lastBotResult.webSocketSpecificData.botResultOptions.promptOptions.input.type;
        }
        if (this.communicationService.lastHistoryBotResult?.webSocketSpecificData?.botResultOptions?.promptOptions?.input?.type !== undefined) {
            return this.communicationService.lastHistoryBotResult.webSocketSpecificData.botResultOptions.promptOptions.input.type;
        }
        return this.initialInputType;
    }

    public get inputOptions(): DialogConfigInputOptionType | undefined {
        if (this.communicationService.lastBotResult?.webSocketSpecificData?.botResultOptions?.promptOptions?.input?.options !== undefined) {
            return this.communicationService.lastBotResult.webSocketSpecificData.botResultOptions.promptOptions.input.options;
        }
        if (this.communicationService.lastHistoryBotResult?.webSocketSpecificData?.botResultOptions?.promptOptions?.input?.options !== undefined) {
            return this.communicationService.lastHistoryBotResult.webSocketSpecificData.botResultOptions.promptOptions.input.options;
        }
        return this.initialInputOptions;
    }

    public ngOnInit(): void {
        if (this.botId !== undefined) {
            this.communicationService.botId = this.botId;
        }

        this.dialogViewModelCreator.getDialogViewModelObservable().subscribe((dialogViewModel) => {
            this.dialogViewModel = dialogViewModel;
            this.changeDetectorRef.markForCheck();
        });

        this.subscription.add(this.chatManagerService.scrollToBottom$.subscribe((useSmoothScrolling) => this.scrollToBottom(useSmoothScrolling)));


        this.subscription.add(this.communicationService.connectionStateChanged.subscribe((state: boolean) => {
            this.isConnected = state;
            this.changeDetectorRef.markForCheck();
            this.applicationRef.tick();
        }));

        this.subscription.add(this.chatManagerService.conversationHasHistory$.subscribe((state: boolean) => {
            this.showEndOfHistoryIndicator = state;
            this.changeDetectorRef.markForCheck();
        }));
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public ngOnChanges(changes: { [propertyName: string]: SimpleChange }): void {
        if (changes['clientId'] !== undefined && this.clientId !== undefined) {
            this.communicationService.clientId = this.clientId;
        }

        // TODO: change botId
        if (changes['botId'] !== undefined && this.botId !== undefined) {
            this.communicationService.botId = this.botId;
        }
    }

    public async selectQuickReply(quickReply: QuickReplyDto): Promise<void> {
        const message = quickReply.sendText !== undefined ? quickReply.sendText : quickReply.displayText;
        this.onQuickReplyClick.emit(quickReply);
        await this.sendMessage(message, 'quickreply');
    }

    public async selectItem(message: string): Promise<void> {
        await this.sendMessage(message, 'selectionlist');
    }

    public botAvatar(messageGroup: MessageGroupViewModel): boolean {
        let messageNumeration;
        if (this.dialogViewModel.messageGroups[0].sender === 'client') {
            messageNumeration = 4;
        } else {
            messageNumeration = 1;
        }
        let status: boolean = false;
        if (this.dialogViewModel.messageGroups.indexOf(messageGroup) > messageNumeration) {
            if (this.dialogViewModel.messageGroups[this.dialogViewModel.messageGroups.indexOf(messageGroup) - 2].botAvatarImageUrl !== undefined) {
                status = true;
            }
        }
        return status;
    }

    public getMessageGroupKey(index: number, item: MessageGroupViewModel): number {
        return item.id;
    }

    public getMessageKey(index: number, item: MessageViewModel): string {
        return btoa(encodeURIComponent(JSON.stringify(item)));
    }

    private async sendMessage(text: string, inputSource: InputSourceType): Promise<void> {
        this.dialogViewModelCreator.addUserTextMessage(text);
        await this.communicationService.sendMessage(text, inputSource);
    }

    private scrollToBottom(useSmoothScrolling: boolean): void {
        setTimeout(() => {
            const htmlElement = (this.dialogDiv.nativeElement as HTMLElement);

            let scrollBehaviour: ScrollBehavior = 'instant';
            if (useSmoothScrolling) {
                scrollBehaviour = 'smooth';
            }

            const lastChild = htmlElement.lastElementChild ?? undefined;
            if (lastChild === undefined) {
                return;
            }
            lastChild.scrollIntoView({
                behavior: scrollBehaviour,
                block: 'end'
            });
        }, 10);
    }


}
